import React, { JSX, useEffect, useMemo, useRef, useState } from 'react';
import {
  Controller,
  FieldPath,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';

import { AnimatePresence, motion } from 'framer-motion';
import {
  CheckIcon,
  ChevronDown,
  ChevronUp,
  Info,
  Package,
  PencilIcon,
  TrashIcon,
  XIcon,
} from 'lucide-react';

import { ActionButton } from 'components/ActionButton';
import { Checkbox } from 'components/Checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'components/Collapsible';
import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import type {
  BaseOptionType,
  SelectValue as SelectValueType,
} from 'components/Select';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Tooltip';
import { Input } from 'components/input';
import { RHFCommitOnBlurNumberInput } from 'components/input/RHFCommitOnBlurNumberInput';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { useToast } from 'hooks/useToaster';
import { getThreeGCommodities } from 'lib/api/getThreeGCommodities';
import { searchCommodities } from 'lib/api/searchCommodities';
import { TMSCommodity } from 'types/Load';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';
import { calculateDensity } from 'utils/calculateDensity';
import { densityToFreightClass } from 'utils/densityToFreightClass';

import { useQuickQuoteContext } from '../../context/QuickQuoteContext';
import {
  HANDLING_UNIT_TYPES_THREEG,
  HAZMAT_CLASS_OPTIONS_THREEG,
  LTLCustomer,
  LTLItem,
  LTL_FREIGHT_CLASSES,
  PACKING_GROUP_OPTIONS_THREEG,
  QuickQuoteInputs,
} from '../../types';
import { formatUpToFiveDecimals } from '../../utils/formatting';
import { getColorForItemId } from '../components/visualizationColors';
import { useIs3DVisualisationEnabled } from '../hooks/useLTLQuickQuoteConfigFlags';
import {
  useAutoOpenSplitSideLoadRow,
  useSideLoadItemStatusTag,
} from '../hooks/useSideLoadItemStatusTag';
import { CARD_ANIMATIONS as ANIMATIONS } from './cardAnimations';
import { getThreeGFreightClassAutoApplyUpdate } from './freightClassAutoApply';

interface ItemCardProps {
  index: number;
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  itemColorMap: ReadonlyMap<string, string>;
  onRemove: () => void;
  commodities: TMSCommodity[];
  isLoadingCommodities: boolean;
  hasCustomer: boolean;
  hasCustomerId: boolean;
}

const FIELD_PATH = 'ltl.items' as const;
const DEFAULT_HANDLING_UNIT_TYPE =
  HANDLING_UNIT_TYPES_THREEG.find((opt) => opt.value === '3')?.value ?? '3';

/** Constrain dropdown list height so it scrolls inside the item card (consistent with other LTL item cards). */
const SELECT_CONTENT_MAX_HEIGHT = 'max-h-60';
const ITEM_LABEL_CLASS_NAME =
  'whitespace-normal leading-tight break-words flex-wrap';

type DescriptionOption = BaseOptionType & {
  freightClass?: string;
  nmfcCode?: string;
  commodityName?: string;
  isCustom?: boolean;
  renderLabel?: string;
};

type CustomerForCommodityLookup = Pick<
  LTLCustomer,
  'customerDbId' | 'customerId' | 'externalId'
>;

type ClassScopedCommodityLookup = {
  tmsId: number;
  customerId: Undef<number>;
  customerExternalId: Undef<string>;
  fallbackExternalId: Undef<string>;
  freightClass: string;
};

function isValidNumber(val: unknown): val is number {
  return typeof val === 'number' && !isNaN(val) && val > 0;
}

function getCommodityDisplayName(c: TMSCommodity): string {
  return c.commodityDescription ?? c.commodity ?? c.externalTMSID ?? '';
}

/** Matches filter / mismatch logic: trim `freightClass`, then fall back to `freightClassId` (empty string is not used). */
function getCommodityFreightClassString(c: TMSCommodity): string {
  return (
    c.freightClass?.trim() ||
    (c.freightClassId != null ? String(c.freightClassId) : '')
  );
}

/** Stable key for length/width/height/weight/handling units (must match apply + effect). */
function dimsKeyFromFields(
  length: unknown,
  width: unknown,
  height: unknown,
  weight: unknown,
  handlingUnits: unknown
): string {
  return [
    String(length ?? ''),
    String(width ?? ''),
    String(height ?? ''),
    String(weight ?? ''),
    String(handlingUnits ?? ''),
  ].join('|');
}

function getCustomerId(
  customer: MaybeUndef<CustomerForCommodityLookup>
): Undef<number> {
  if (customer?.customerDbId != null) {
    return customer.customerDbId;
  }

  const parsed = Number.parseInt(customer?.customerId ?? '', 10);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function getCustomerExternalId(
  customer: MaybeUndef<CustomerForCommodityLookup>
): Undef<string> {
  return customer?.customerId ?? customer?.externalId ?? undefined;
}

function getFallbackExternalId(
  customer: MaybeUndef<CustomerForCommodityLookup>
): Undef<string> {
  // `customerId` can be the internal id or a legacy external TMS id; `externalId`
  // is the explicit TMS id when present. Try it if the primary lookup found no rows.
  if (customer?.externalId && customer.externalId !== customer.customerId) {
    return customer.externalId;
  }

  return undefined;
}

function getClassScopedCommodityLookup({
  enableAutoFreightClass,
  freightClass,
  tmsId,
  customerId,
  customerExternalId,
  fallbackExternalId,
}: {
  enableAutoFreightClass: boolean;
  freightClass: Maybe<string>;
  tmsId: Undef<number>;
  customerId: Undef<number>;
  customerExternalId: Undef<string>;
  fallbackExternalId: Undef<string>;
}): Maybe<ClassScopedCommodityLookup> {
  if (
    !enableAutoFreightClass ||
    !freightClass ||
    !tmsId ||
    (!customerId && !customerExternalId)
  ) {
    return null;
  }

  return {
    tmsId,
    customerId,
    customerExternalId,
    fallbackExternalId,
    freightClass,
  };
}

// ThreeG LTL item card with commodity selection and per-item dimensions/weight.
export function ItemCard({
  index,
  formMethods,
  itemColorMap,
  onRemove,
  commodities,
  isLoadingCommodities,
  hasCustomer,
  hasCustomerId,
}: ItemCardProps): JSX.Element {
  const {
    setValue,
    resetField,
    control,
    formState: { errors, defaultValues },
  } = formMethods;
  const { ltlCustomer } = useQuickQuoteContext();
  const { ltlTMSIntegrations } = useServiceFeatures();
  const { toast } = useToast();
  const enableAutoFreightClass = Boolean(ltlCustomer?.enableAutoFreightClass);
  const [isEditing, setIsEditing] = useState(false);
  const [originalValues, setOriginalValues] =
    useState<Maybe<Partial<LTLItem>>>(null);
  const [searchValue, setSearchValue] = useState('');
  const [searchedCommodities, setSearchedCommodities] = useState<
    TMSCommodity[]
  >([]);
  const [classScopedCommodities, setClassScopedCommodities] = useState<
    TMSCommodity[]
  >([]);
  const [isSearchingCommodities, setIsSearchingCommodities] = useState(false);
  const [isLoadingClassScopedCommodities, setIsLoadingClassScopedCommodities] =
    useState(false);
  const [classScopedCommodityFetchFailed, setClassScopedCommodityFetchFailed] =
    useState(false);
  const [isAdditionalDetailsOpen, setIsAdditionalDetailsOpen] = useState(false);
  const commoditySearchRequestIdRef = useRef(0);
  const showVisualizationColor = useIs3DVisualisationEnabled();
  const classScopedCommodityRequestIdRef = useRef(0);

  // After applying a commodity, dims/weight that came from that apply — skip
  // implied-class sync/clear until user edits.
  const commodityApplyDimsKeyRef = useRef<string | null>(null);

  const watchedItem = useWatch({
    control,
    name: `${FIELD_PATH}.${index}`,
  });
  const { renderSideLoadStatusTag } = useSideLoadItemStatusTag({
    control,
    itemIndex: index,
    parentItemLabelPrefix: 'Commodity',
  });
  const [length, width, height, weight, handlingUnits] = useWatch({
    control,
    name: [
      `${FIELD_PATH}.${index}.length`,
      `${FIELD_PATH}.${index}.width`,
      `${FIELD_PATH}.${index}.height`,
      `${FIELD_PATH}.${index}.weight`,
      `${FIELD_PATH}.${index}.handlingUnits`,
    ],
  });
  const isEmptyItem = !watchedItem?.description;
  const parsedLength = Number(length);
  const parsedWidth = Number(width);
  const parsedHeight = Number(height);
  const parsedWeight = Number(weight);
  const parsedHandlingUnits = Number(handlingUnits);
  const effectiveHandlingUnits =
    Number.isFinite(parsedHandlingUnits) && parsedHandlingUnits > 0
      ? parsedHandlingUnits
      : 1;
  const itemCubicFeet = useMemo(() => {
    if (
      !Number.isFinite(parsedLength) ||
      !Number.isFinite(parsedWidth) ||
      !Number.isFinite(parsedHeight) ||
      parsedLength <= 0 ||
      parsedWidth <= 0 ||
      parsedHeight <= 0
    ) {
      return 0;
    }

    return (
      ((parsedLength * parsedWidth * parsedHeight) / 1728) *
      effectiveHandlingUnits
    );
  }, [effectiveHandlingUnits, parsedHeight, parsedLength, parsedWidth]);

  const calculatedDensityPcf = useMemo(
    () =>
      calculateDensity(
        Number.isFinite(parsedWeight) && parsedWeight > 0 ? parsedWeight : null,
        1,
        Number.isFinite(parsedLength) && parsedLength > 0 ? parsedLength : null,
        Number.isFinite(parsedWidth) && parsedWidth > 0 ? parsedWidth : null,
        Number.isFinite(parsedHeight) && parsedHeight > 0 ? parsedHeight : null,
        0,
        effectiveHandlingUnits
      ),
    [
      effectiveHandlingUnits,
      parsedHeight,
      parsedLength,
      parsedWidth,
      parsedWeight,
    ]
  );

  const pcf = calculatedDensityPcf ?? 0;

  const densityDerivedFreightClassString =
    pcf > 0 ? densityToFreightClass(pcf) : null;
  const currentFreightClassString = watchedItem?.freightClass?.trim() ?? '';
  const isFreightClassLocked = Boolean(watchedItem?.freightClassLocked);
  const freightClassForCommodityFilter =
    currentFreightClassString || densityDerivedFreightClassString;

  const tmsId = ltlTMSIntegrations?.find((i) => i.name === TMS.ThreeG)?.id;
  const customerId = getCustomerId(ltlCustomer);
  const customerExternalId = getCustomerExternalId(ltlCustomer);
  const fallbackExternalId = getFallbackExternalId(ltlCustomer);

  const classScopedCommodityLookup = useMemo(
    () =>
      getClassScopedCommodityLookup({
        enableAutoFreightClass,
        freightClass: freightClassForCommodityFilter,
        tmsId,
        customerId,
        customerExternalId,
        fallbackExternalId,
      }),
    [
      customerExternalId,
      customerId,
      enableAutoFreightClass,
      fallbackExternalId,
      freightClassForCommodityFilter,
      tmsId,
    ]
  );

  const shouldFetchClassScopedCommodities = classScopedCommodityLookup != null;

  useEffect(() => {
    if (!classScopedCommodityLookup) {
      classScopedCommodityRequestIdRef.current += 1;
      setClassScopedCommodities([]);
      setIsLoadingClassScopedCommodities(false);
      setClassScopedCommodityFetchFailed(false);
      return;
    }

    const requestId = ++classScopedCommodityRequestIdRef.current;
    setIsLoadingClassScopedCommodities(true);
    setClassScopedCommodityFetchFailed(false);
    setClassScopedCommodities([]);

    const fetchClassScopedCommodities = async () => {
      const getClassScopedCommoditiesForExternalId = async (
        externalTmsId: Undef<string>
      ) => {
        const res = await getThreeGCommodities(
          classScopedCommodityLookup.tmsId,
          {
            customerID: classScopedCommodityLookup.customerId,
            externalTmsId,
            forceRefresh: false,
            freightClass: classScopedCommodityLookup.freightClass,
          }
        );

        if (requestId !== classScopedCommodityRequestIdRef.current) {
          return undefined;
        }

        return res;
      };

      const res = await getClassScopedCommoditiesForExternalId(
        classScopedCommodityLookup.customerExternalId
      );

      if (!res) {
        return;
      }

      if (res.isOk()) {
        let commodityList = res.value.commodityList ?? [];
        if (
          !classScopedCommodityLookup.customerId &&
          commodityList.length === 0 &&
          classScopedCommodityLookup.fallbackExternalId
        ) {
          const fallbackRes = await getClassScopedCommoditiesForExternalId(
            classScopedCommodityLookup.fallbackExternalId
          );

          if (!fallbackRes) {
            return;
          }

          if (fallbackRes.isOk()) {
            commodityList = fallbackRes.value.commodityList ?? [];
          } else {
            setClassScopedCommodityFetchFailed(true);
            setIsLoadingClassScopedCommodities(false);
            toast({
              description:
                fallbackRes.error.message ?? 'Failed to load commodities',
              variant: 'destructive',
            });
            return;
          }
        }

        setClassScopedCommodities(commodityList);
        setClassScopedCommodityFetchFailed(false);
        setIsLoadingClassScopedCommodities(false);
        return;
      }

      setClassScopedCommodityFetchFailed(true);
      setIsLoadingClassScopedCommodities(false);
      toast({
        description: res.error.message ?? 'Failed to load commodities',
        variant: 'destructive',
      });
    };

    fetchClassScopedCommodities();

    return () => {
      classScopedCommodityRequestIdRef.current += 1;
    };
  }, [classScopedCommodityLookup, toast]);

  const shouldUseClassScopedCommodities =
    shouldFetchClassScopedCommodities && !classScopedCommodityFetchFailed;

  const baseCommoditiesForDropdown = shouldUseClassScopedCommodities
    ? classScopedCommodities
    : commodities;

  const commoditiesForDropdown = useMemo(() => {
    if (!enableAutoFreightClass || freightClassForCommodityFilter == null) {
      return baseCommoditiesForDropdown;
    }

    return baseCommoditiesForDropdown.filter(
      (c) =>
        getCommodityFreightClassString(c) === freightClassForCommodityFilter
    );
  }, [
    baseCommoditiesForDropdown,
    enableAutoFreightClass,
    freightClassForCommodityFilter,
  ]);

  const applyCommoditySelection = (commodity: TMSCommodity) => {
    setValue(
      `${FIELD_PATH}.${index}.description`,
      getCommodityDisplayName(commodity),
      {
        shouldValidate: true,
        shouldDirty: true,
      }
    );
    if (!isFreightClassLocked) {
      setValue(
        `${FIELD_PATH}.${index}.freightClass`,
        getCommodityFreightClassString(commodity),
        {
          shouldValidate: true,
          shouldDirty: true,
        }
      );
    }
    setValue(
      `${FIELD_PATH}.${index}.nmfcCode`,
      commodity.nmfc ?? commodity.nmfcNumber ?? '',
      {
        shouldValidate: true,
        shouldDirty: true,
      }
    );
    setValue(
      `${FIELD_PATH}.${index}.commodityExternalTmsId`,
      commodity.externalTMSID ?? '',
      {
        shouldValidate: true,
        shouldDirty: true,
      }
    );
    setValue(`${FIELD_PATH}.${index}.freightClassLocked`, true, {
      shouldDirty: true,
    });

    if (commodity.length) {
      setValue(`${FIELD_PATH}.${index}.length`, commodity.length, {
        shouldDirty: true,
      });
    }
    if (commodity.width) {
      setValue(`${FIELD_PATH}.${index}.width`, commodity.width, {
        shouldDirty: true,
      });
    }
    if (commodity.height) {
      setValue(`${FIELD_PATH}.${index}.height`, commodity.height, {
        shouldDirty: true,
      });
    }
    if (commodity.weightAmount && commodity.weightAmount > 0) {
      setValue(`${FIELD_PATH}.${index}.weight`, commodity.weightAmount, {
        shouldDirty: true,
      });
    }
    if (commodity.handlingUnitCount && commodity.handlingUnitCount > 0) {
      setValue(
        `${FIELD_PATH}.${index}.handlingUnits`,
        commodity.handlingUnitCount,
        {
          shouldDirty: true,
        }
      );
    }
    if (commodity.pieceCount && commodity.pieceCount > 0) {
      setValue(`${FIELD_PATH}.${index}.pieces`, commodity.pieceCount, {
        shouldDirty: true,
      });
    }

    // Must match the setValue guards above (0 is unset in TMS → we keep prior form values).
    const nextLength = commodity.length ? commodity.length : length;
    const nextWidth = commodity.width ? commodity.width : width;
    const nextHeight = commodity.height ? commodity.height : height;
    const nextWeight =
      commodity.weightAmount && commodity.weightAmount > 0
        ? commodity.weightAmount
        : weight;
    const nextHandlingUnits =
      commodity.handlingUnitCount && commodity.handlingUnitCount > 0
        ? commodity.handlingUnitCount
        : handlingUnits;

    commodityApplyDimsKeyRef.current = dimsKeyFromFields(
      nextLength,
      nextWidth,
      nextHeight,
      nextWeight,
      nextHandlingUnits
    );
  };

  const descriptionPlaceholder = !hasCustomer
    ? 'Select a customer first to load commodities'
    : !hasCustomerId
      ? 'Commodities unavailable for this customer'
      : isLoadingCommodities || isLoadingClassScopedCommodities
        ? 'Loading commodities...'
        : commoditiesForDropdown.length === 0
          ? 'No commodities found'
          : 'Select or type a description';

  const commodityOptions = useMemo<DescriptionOption[]>(
    () =>
      commoditiesForDropdown.map((c) => ({
        label: getCommodityDisplayName(c),
        value: c.externalTMSID,
        freightClass: getCommodityFreightClassString(c) || undefined,
        nmfcCode: c.nmfc ?? c.nmfcNumber,
        commodityName: c.commodity,
      })),
    [commoditiesForDropdown]
  );
  const searchedCommoditiesForDropdown = useMemo(() => {
    if (!enableAutoFreightClass || freightClassForCommodityFilter == null) {
      return searchedCommodities;
    }

    return searchedCommodities.filter(
      (c) =>
        getCommodityFreightClassString(c) === freightClassForCommodityFilter
    );
  }, [
    enableAutoFreightClass,
    freightClassForCommodityFilter,
    searchedCommodities,
  ]);

  const descriptionValue = watchedItem?.description?.trim() ?? '';
  const customerIdForSearch = ltlCustomer?.customerDbId;
  const trimmedSearchValue = searchValue.trim();
  const shouldUseCommoditySearchAPI = commodities.length >= 100;
  const hasActiveSearch =
    shouldUseCommoditySearchAPI &&
    trimmedSearchValue.length >= 2 &&
    Boolean(tmsId && customerIdForSearch);

  useEffect(() => {
    if (!hasActiveSearch || !tmsId || !customerIdForSearch) {
      commoditySearchRequestIdRef.current += 1;
      setSearchedCommodities([]);
      setIsSearchingCommodities(false);
      return;
    }

    const requestId = ++commoditySearchRequestIdRef.current;
    const timer = setTimeout(async () => {
      setIsSearchingCommodities(true);
      const result = await searchCommodities(
        tmsId,
        customerIdForSearch,
        trimmedSearchValue,
        enableAutoFreightClass
          ? (freightClassForCommodityFilter ?? undefined)
          : undefined
      );

      if (requestId !== commoditySearchRequestIdRef.current) {
        return;
      }

      if (result.isOk()) {
        setSearchedCommodities(result.value.commodityList ?? []);
        setIsSearchingCommodities(false);
        return;
      }

      setSearchedCommodities([]);
      setIsSearchingCommodities(false);
      toast({
        description: result.error.message ?? 'Failed to search commodities',
        variant: 'destructive',
      });
    }, 300);

    return () => {
      commoditySearchRequestIdRef.current += 1;
      clearTimeout(timer);
    };
  }, [
    customerIdForSearch,
    enableAutoFreightClass,
    freightClassForCommodityFilter,
    hasActiveSearch,
    shouldUseCommoditySearchAPI,
    tmsId,
    toast,
    trimmedSearchValue,
  ]);

  const selectedCommodityFallback = useMemo(() => {
    if (!watchedItem?.commodityExternalTmsId) {
      return undefined;
    }

    return {
      externalTMSID: watchedItem.commodityExternalTmsId,
      commodityDescription: watchedItem.description ?? '',
      freightClass: watchedItem.freightClass ?? '',
      nmfc: watchedItem.nmfcCode ?? '',
    } as TMSCommodity;
  }, [
    watchedItem?.commodityExternalTmsId,
    watchedItem?.description,
    watchedItem?.freightClass,
    watchedItem?.nmfcCode,
  ]);

  const allCommodityOptions = useMemo(() => {
    const byExternalId = new Map<string, TMSCommodity>();
    for (const commodity of [
      ...(selectedCommodityFallback ? [selectedCommodityFallback] : []),
      ...commodities,
      ...classScopedCommodities,
      ...searchedCommodities,
    ]) {
      if (commodity.externalTMSID) {
        byExternalId.set(commodity.externalTMSID, commodity);
      }
    }
    return Array.from(byExternalId.values());
  }, [
    classScopedCommodities,
    commodities,
    searchedCommodities,
    selectedCommodityFallback,
  ]);

  const selectedCommodity = useMemo(() => {
    if (!watchedItem?.commodityExternalTmsId) {
      return undefined;
    }

    return allCommodityOptions.find(
      (c) => c.externalTMSID === watchedItem.commodityExternalTmsId
    );
  }, [allCommodityOptions, watchedItem?.commodityExternalTmsId]);

  // 3G auto-freight-class flow:
  // compute implied freight class from dimensions/weight/handling units
  // use density-derived freight class only when the field is unlocked
  // if a commodity is selected but no longer matches, clear it so the user re-selects
  useEffect(() => {
    if (!watchedItem?.commodityExternalTmsId) {
      commodityApplyDimsKeyRef.current = null;
    }

    const currentDimsKey = dimsKeyFromFields(
      length,
      width,
      height,
      weight,
      handlingUnits
    );
    const dimsStillMatchAppliedCommodity =
      commodityApplyDimsKeyRef.current != null &&
      commodityApplyDimsKeyRef.current === currentDimsKey &&
      Boolean(watchedItem?.commodityExternalTmsId);

    // While locked, keep the selected commodity's class even if its dimensions imply a
    // different density class. Unlocking immediately resumes density auto-class.
    if (dimsStillMatchAppliedCommodity && isFreightClassLocked) {
      return;
    }

    const autoUpdate = getThreeGFreightClassAutoApplyUpdate({
      currentFreightClass: currentFreightClassString,
      densityDerivedFreightClass: densityDerivedFreightClassString,
      isFreightClassLocked,
    });

    if (autoUpdate) {
      setValue(`${FIELD_PATH}.${index}.freightClass`, autoUpdate, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    if (densityDerivedFreightClassString == null) {
      return;
    }

    const effectiveFreightClass = autoUpdate ?? currentFreightClassString;
    if (effectiveFreightClass !== densityDerivedFreightClassString) {
      return;
    }

    if (!enableAutoFreightClass) {
      return;
    }

    const hasSelectedCommodity = Boolean(watchedItem?.commodityExternalTmsId);
    if (!hasSelectedCommodity) {
      return;
    }

    // Avoid clearing while commodity list is still loading.
    if (isLoadingCommodities || isLoadingClassScopedCommodities) {
      return;
    }

    const selectedFreightClass = selectedCommodity
      ? getCommodityFreightClassString(selectedCommodity)
      : '';
    if (selectedFreightClass !== densityDerivedFreightClassString) {
      commodityApplyDimsKeyRef.current = null;
      setValue(`${FIELD_PATH}.${index}.commodityExternalTmsId`, '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue(`${FIELD_PATH}.${index}.description`, '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue(`${FIELD_PATH}.${index}.nmfcCode`, '', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [
    currentFreightClassString,
    enableAutoFreightClass,
    handlingUnits,
    height,
    densityDerivedFreightClassString,
    index,
    isFreightClassLocked,
    isLoadingClassScopedCommodities,
    isLoadingCommodities,
    length,
    selectedCommodity,
    setValue,
    watchedItem?.commodityExternalTmsId,
    weight,
    width,
  ]);

  const selectValue = selectedCommodity
    ? selectedCommodity.externalTMSID
    : descriptionValue
      ? `custom:${descriptionValue}`
      : '';

  const selectOptions = useMemo(() => {
    const baseOptions = [
      ...(hasActiveSearch
        ? searchedCommoditiesForDropdown.map((c) => ({
            label: getCommodityDisplayName(c),
            value: c.externalTMSID,
            freightClass: getCommodityFreightClassString(c) || undefined,
            nmfcCode: c.nmfc ?? c.nmfcNumber,
            commodityName: c.commodity,
          }))
        : commodityOptions),
    ];
    const options = [...baseOptions];
    if (
      selectedCommodity?.externalTMSID &&
      !options.some((opt) => opt.value === selectedCommodity.externalTMSID)
    ) {
      options.unshift({
        label: getCommodityDisplayName(selectedCommodity),
        value: selectedCommodity.externalTMSID,
        freightClass:
          getCommodityFreightClassString(selectedCommodity) || undefined,
        nmfcCode: selectedCommodity.nmfc ?? selectedCommodity.nmfcNumber,
        commodityName: selectedCommodity.commodity,
      });
    }
    const normalizedSearch = searchValue.trim();
    const isSearchMatchingCommodity = normalizedSearch
      ? baseOptions.some((option) => {
          const label = String(option.label).toLowerCase();
          return (
            label === normalizedSearch.toLowerCase() ||
            String(option.value).toLowerCase() ===
              normalizedSearch.toLowerCase()
          );
        })
      : false;
    const customOption = descriptionValue
      ? {
          label: descriptionValue,
          value: `custom:${descriptionValue}`,
          isCustom: true,
          renderLabel: descriptionValue,
        }
      : null;
    const searchOption =
      normalizedSearch && !isSearchMatchingCommodity
        ? {
            label: normalizedSearch,
            value: `custom:${normalizedSearch}`,
            isCustom: true,
            renderLabel: `Use "${normalizedSearch}"`,
          }
        : null;

    if (
      customOption &&
      !options.some((opt) => opt.value === customOption.value)
    ) {
      options.unshift(customOption);
    }

    if (
      searchOption &&
      !options.some((opt) => opt.value === searchOption.value)
    ) {
      options.unshift(searchOption);
    }

    return options;
  }, [
    commodityOptions,
    descriptionValue,
    hasActiveSearch,
    searchedCommoditiesForDropdown,
    selectedCommodity,
    searchValue,
  ]);

  const filterOption = (input: string, option?: BaseOptionType) => {
    if (!option) {
      return false;
    }

    const typedOption = option as DescriptionOption;
    if (typedOption.isCustom) {
      return true;
    }

    const search = input.toLowerCase();

    return (
      String(typedOption.label ?? '')
        .toLowerCase()
        .includes(search) ||
      (typedOption.commodityName ?? '').toLowerCase().includes(search) ||
      (typedOption.nmfcCode ?? '').toLowerCase().includes(search)
    );
  };

  const handleDescriptionSelect = (
    value: SelectValueType,
    option?: BaseOptionType | BaseOptionType[]
  ) => {
    const selectedOption = Array.isArray(option) ? option[0] : option;
    const selectedValue =
      typeof value === 'string' || typeof value === 'number'
        ? String(value)
        : '';

    if (selectedOption?.isCustom) {
      const customLabel = String(selectedOption.label ?? '').trim();
      const defaultItem = (defaultValues?.ltl as any)?.items?.[index];

      if (defaultItem && customLabel === defaultItem.description) {
        const fieldsToReset: (keyof LTLItem)[] = [
          'description',
          'freightClass',
          'freightClassLocked',
          'nmfcCode',
          'commodityExternalTmsId',
          'length',
          'width',
          'height',
          'weight',
          'handlingUnits',
          'pieces',
          'handlingUnitType',
          'linearLength',
          'minTemp',
          'maxTemp',
          'nonStackable',
          'hazmat',
          'unClass',
          'hazmatContactNumber',
          'packingGroup',
          'hazmatClass',
          'properShippingName',
          'specialProvisions',
          'subsidiaryClass',
          'hazmatContactName',
          'erapReference',
        ];
        fieldsToReset.forEach((key) => {
          resetField(`${FIELD_PATH}.${index}.${key}` as any);
        });
        commodityApplyDimsKeyRef.current = null;
      } else {
        const hadSelectedCommodity = Boolean(
          watchedItem?.commodityExternalTmsId
        );

        setValue(`${FIELD_PATH}.${index}.description`, customLabel, {
          shouldValidate: true,
          shouldDirty: true,
        });

        if (hadSelectedCommodity) {
          setValue(`${FIELD_PATH}.${index}.freightClass`, '', {
            shouldValidate: true,
            shouldDirty: true,
          });
          setValue(`${FIELD_PATH}.${index}.freightClassLocked`, false, {
            shouldDirty: true,
          });

          setValue(`${FIELD_PATH}.${index}.nmfcCode`, '', {
            shouldValidate: true,
            shouldDirty: true,
          });

          setValue(`${FIELD_PATH}.${index}.length`, undefined, {
            shouldValidate: true,
            shouldDirty: true,
          });

          setValue(`${FIELD_PATH}.${index}.width`, undefined, {
            shouldValidate: true,
            shouldDirty: true,
          });

          setValue(`${FIELD_PATH}.${index}.height`, undefined, {
            shouldValidate: true,
            shouldDirty: true,
          });

          setValue(`${FIELD_PATH}.${index}.weight`, undefined, {
            shouldValidate: true,
            shouldDirty: true,
          });

          setValue(`${FIELD_PATH}.${index}.handlingUnits`, undefined, {
            shouldValidate: true,
            shouldDirty: true,
          });

          setValue(`${FIELD_PATH}.${index}.pieces`, undefined, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }

        setValue(`${FIELD_PATH}.${index}.commodityExternalTmsId`, '', {
          shouldValidate: true,
          shouldDirty: true,
        });
        commodityApplyDimsKeyRef.current = null;
      }
      setSearchValue('');
      return;
    }

    const commodity = allCommodityOptions.find(
      (c) => c.externalTMSID === selectedValue
    );
    if (commodity) {
      applyCommoditySelection(commodity);
    }
    setSearchValue('');
  };

  const dimensionUnit = 'in';
  const weightUnit = 'lb';
  const isHazmat = Boolean(watchedItem?.hazmat);

  useEffect(() => {
    if (isEmptyItem && !isEditing) {
      setIsEditing(true);
    }
  }, [isEmptyItem, isEditing]);

  useAutoOpenSplitSideLoadRow({
    watchedItem,
    isEditing,
    setIsEditing,
  });

  const handlers = {
    edit: () => {
      setOriginalValues({ ...watchedItem });
      setIsEditing(true);
    },
    save: () => {
      setIsEditing(false);
      setOriginalValues(null);
    },
    discard: () => {
      if (originalValues) {
        const fullSnapshot: LTLItem = {
          ...originalValues,
          itemId: originalValues.itemId ?? watchedItem?.itemId ?? '',
          description: originalValues.description ?? '',
        };
        setValue(
          `${FIELD_PATH}.${index}` as FieldPath<QuickQuoteInputs>,
          fullSnapshot,
          { shouldValidate: true }
        );
        setIsEditing(false);
        setOriginalValues(null);
      } else {
        onRemove();
      }
    },
    remove: onRemove,
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && isEditing) {
      e.preventDefault();
      e.stopPropagation();
      handlers.save();
    }
  };

  const renderActions = () => (
    <AnimatePresence mode='wait'>
      {isEditing ? (
        <motion.div
          key='editing-actions'
          {...ANIMATIONS.actions}
          className='flex gap-1.5'
        >
          {!isEmptyItem ? (
            <>
              <ActionButton
                onClick={handlers.save}
                className='hover:bg-info-200 text-info-600'
                icon={CheckIcon}
                aria-label='Save changes'
              />
              <ActionButton
                onClick={handlers.discard}
                className='hover:bg-error-200 text-error-600'
                icon={XIcon}
                aria-label='Discard changes'
              />
            </>
          ) : (
            <ActionButton
              onClick={handlers.remove}
              className='hover:bg-error-300 text-error-600'
              icon={TrashIcon}
              aria-label='Remove item'
            />
          )}
        </motion.div>
      ) : (
        <motion.div
          key='view-actions'
          {...ANIMATIONS.actions}
          className='flex gap-1.5'
        >
          <ActionButton
            onClick={handlers.edit}
            className='hover:bg-info-300 text-info-600'
            icon={PencilIcon}
            aria-label='Edit item'
          />
          <ActionButton
            onClick={handlers.remove}
            className='hover:bg-error-400 text-error-600'
            icon={TrashIcon}
            aria-label='Remove item'
          />
        </motion.div>
      )}
    </AnimatePresence>
  );

  const renderEditingForm = () => (
    <motion.div
      key='editing-form'
      {...ANIMATIONS.toggle}
      className='overflow-hidden'
    >
      <Flex justify='between' align='center' className='mb-3'>
        <Flex align='center' gap='sm' className='mr-2'>
          <Typography
            variant='body-sm'
            weight='medium'
            className='whitespace-nowrap shrink-0'
          >
            Edit Item
          </Typography>
          {renderSideLoadStatusTag('max-w-[150px]')}
        </Flex>
        <div onClick={(e) => e.stopPropagation()}>{renderActions()}</div>
      </Flex>
      <div className='mb-4'>
        <Label
          name={`${FIELD_PATH}.${index}.description`}
          required
          className={ITEM_LABEL_CLASS_NAME}
        >
          Description
        </Label>
        <Controller
          name={`${FIELD_PATH}.${index}.description`}
          control={control}
          rules={{ required: 'Description is required' }}
          render={() => (
            <Select
              value={selectValue}
              onChange={handleDescriptionSelect}
              options={selectOptions}
              showSearch
              onSearch={setSearchValue}
              disabled={isLoadingClassScopedCommodities}
              placeholder={
                isSearchingCommodities
                  ? 'Searching commodities...'
                  : descriptionPlaceholder
              }
              className='w-full mt-1'
              filterOption={hasActiveSearch ? false : filterOption}
              optionRender={(option: DescriptionOption) => {
                if (option.isCustom) {
                  return (
                    <span className='block whitespace-normal break-words'>
                      {option.renderLabel ?? option.label}
                    </span>
                  );
                }

                return (
                  <span className='block whitespace-normal break-words'>
                    {option.label}
                    {option.freightClass ? ` (CL ${option.freightClass})` : ''}
                  </span>
                );
              }}
            />
          )}
        />
        {errors.ltl?.items?.[index]?.description && (
          <Typography variant='body-xs' className='text-error-500 mt-1'>
            {errors.ltl.items[index].description.message}
          </Typography>
        )}
      </div>

      <div className='mb-4'>
        <Controller
          name={`${FIELD_PATH}.${index}.freightClassLocked`}
          control={control}
          render={({ field }) => (
            <Checkbox
              label='Lock Freight Class'
              checked={Boolean(field.value)}
              onCheckedChange={(checked) => field.onChange(Boolean(checked))}
              labelClassName='text-sm text-neutral-700'
            />
          )}
        />
      </div>

      <Grid cols='2' gap='lg' className='w-full mx-0 mb-4'>
        <div>
          <Label name={`${FIELD_PATH}.${index}.freightClass`} required>
            Freight Class
          </Label>
          <Controller
            name={`${FIELD_PATH}.${index}.freightClass`}
            control={control}
            rules={{ required: 'Freight class is required' }}
            render={({ field }) => (
              <Select
                onValueChange={(value) => {
                  field.onChange(value);
                  setValue(`${FIELD_PATH}.${index}.freightClassLocked`, true, {
                    shouldDirty: true,
                  });
                }}
                value={field.value ?? ''}
              >
                <SelectTrigger className='w-full mt-1'>
                  <SelectValue placeholder='Select class' />
                </SelectTrigger>
                <SelectContent className={SELECT_CONTENT_MAX_HEIGHT}>
                  {LTL_FREIGHT_CLASSES.map((fc) => (
                    <SelectItem key={fc} value={fc}>
                      {fc}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.ltl?.items?.[index]?.freightClass && (
            <Typography variant='body-xs' className='text-error-500 mt-1'>
              {errors.ltl?.items?.[index]?.freightClass?.message}
            </Typography>
          )}
        </div>
        <RHFTextInput
          name={`${FIELD_PATH}.${index}.nmfcCode`}
          label='NMFC'
          placeholder='e.g., 62120-01'
        />
        <div>
          <Flex align='center' gap='xs'>
            <Label
              name={`${FIELD_PATH}.${index}.cubicFeetDisplay`}
              required={false}
            >
              Cubic Feet
            </Label>
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <Info className='h-4 w-4 cursor-help text-neutral-400' />
              </TooltipTrigger>
              <TooltipContent className='mr-1 font-normal max-w-60 whitespace-pre-wrap'>
                <p>
                  Cubic feet for this item (length × width × height × handling
                  units).
                </p>
              </TooltipContent>
            </Tooltip>
          </Flex>
          <Flex gap='xs' className='w-full mx-0 mt-1'>
            <Input
              readOnly
              value={
                itemCubicFeet > 0 ? formatUpToFiveDecimals(itemCubicFeet) : '—'
              }
              className='flex-1'
            />
          </Flex>
        </div>
        <div>
          <Flex align='center' gap='xs'>
            <Label name={`${FIELD_PATH}.${index}.pcfDisplay`} required={false}>
              Density (PCF)
            </Label>
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <Info className='h-4 w-4 cursor-help text-neutral-400' />
              </TooltipTrigger>
              <TooltipContent className='mr-1 font-normal max-w-60 whitespace-pre-wrap'>
                <p>
                  Density (PCF) for this item (lbs/ft³), used to estimate
                  freight class.
                  {densityDerivedFreightClassString
                    ? ` Implied freight class from density: ${densityDerivedFreightClassString}.`
                    : ''}
                </p>
              </TooltipContent>
            </Tooltip>
          </Flex>
          <Input
            readOnly
            value={pcf > 0 ? `${formatUpToFiveDecimals(pcf)} lbs/ft³` : '—'}
            className='mt-1'
          />
        </div>
      </Grid>

      <Grid cols='3' gap='lg' className='w-full mx-0 mb-4'>
        <RHFCommitOnBlurNumberInput
          name={`${FIELD_PATH}.${index}.length`}
          label='Length'
          placeholder='0'
          aiIconOnly={true}
          min={0}
          step={1}
          inlineDimensionUnit={dimensionUnit}
          data-testid={`${FIELD_PATH}.${index}.length`}
        />
        <RHFCommitOnBlurNumberInput
          name={`${FIELD_PATH}.${index}.width`}
          label='Width'
          placeholder='0'
          aiIconOnly={true}
          min={0}
          step={1}
          inlineDimensionUnit={dimensionUnit}
          data-testid={`${FIELD_PATH}.${index}.width`}
        />
        <RHFCommitOnBlurNumberInput
          name={`${FIELD_PATH}.${index}.height`}
          label='Height'
          placeholder='0'
          aiIconOnly={true}
          min={0}
          step={1}
          inlineDimensionUnit={dimensionUnit}
          data-testid={`${FIELD_PATH}.${index}.height`}
        />
      </Grid>

      <Grid cols='2' gap='lg' align='end' className='w-full mx-0 mb-4'>
        <RHFCommitOnBlurNumberInput
          name={`${FIELD_PATH}.${index}.handlingUnits`}
          label='Handling Units'
          placeholder='1'
          aiIconOnly={true}
          min={1}
          step={1}
          data-testid={`${FIELD_PATH}.${index}.handlingUnits`}
        />
        <div>
          <Label
            name={`${FIELD_PATH}.${index}.handlingUnitType`}
            className={`${ITEM_LABEL_CLASS_NAME}`}
          >
            Handling Unit Type
          </Label>
          <Controller
            name={`${FIELD_PATH}.${index}.handlingUnitType`}
            control={control}
            defaultValue={DEFAULT_HANDLING_UNIT_TYPE}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value ?? ''}>
                <SelectTrigger className='w-full mt-1'>
                  <SelectValue placeholder='Select type' />
                </SelectTrigger>
                <SelectContent className={SELECT_CONTENT_MAX_HEIGHT}>
                  {HANDLING_UNIT_TYPES_THREEG.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
      </Grid>

      <Grid cols='2' gap='md' className='mb-3 w-full mx-0'>
        <RHFTextInput
          name={`${FIELD_PATH}.${index}.pieces`}
          label='Pieces'
          placeholder='1'
          inputType='number'
          aiIconOnly={true}
          options={{ valueAsNumber: true, min: 1 }}
          required={false}
          step={1}
        />
        <RHFTextInput
          name={`${FIELD_PATH}.${index}.weight`}
          label='Total Weight'
          placeholder='0'
          inputType='number'
          aiIconOnly={true}
          options={{ valueAsNumber: true, min: 0 }}
          required={false}
          inlineDimensionUnit={weightUnit}
          step={1}
        />
      </Grid>

      <Collapsible
        open={isAdditionalDetailsOpen}
        onOpenChange={setIsAdditionalDetailsOpen}
        className='w-full mt-4'
      >
        <CollapsibleTrigger
          type='button'
          className='w-full cursor-pointer py-2'
        >
          <Flex align='center' justify='between' className='w-full'>
            <Typography
              variant='body-sm'
              weight='medium'
              className='text-neutral-700'
            >
              Additional item details
            </Typography>
            {isAdditionalDetailsOpen ? (
              <ChevronUp className='h-4 w-4 text-neutral-500' />
            ) : (
              <ChevronDown className='h-4 w-4 text-neutral-500' />
            )}
          </Flex>
        </CollapsibleTrigger>
        <CollapsibleContent className='overflow-hidden transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down'>
          <div className='pt-2'>
            <Grid cols='3' gap='lg' align='end' className='w-full mx-0 mb-4'>
              <RHFTextInput
                name={`${FIELD_PATH}.${index}.linearLength`}
                label='Linear Length'
                placeholder='0'
                inputType='number'
                options={{ valueAsNumber: true, min: 0 }}
                required={false}
                inlineDimensionUnit='Ft'
                step={1}
              />
              <RHFTextInput
                name={`${FIELD_PATH}.${index}.minTemp`}
                label='Min Temp'
                placeholder='0'
                inputType='number'
                options={{ valueAsNumber: true }}
                required={false}
              />
              <RHFTextInput
                name={`${FIELD_PATH}.${index}.maxTemp`}
                label='Max Temp'
                placeholder='0'
                inputType='number'
                options={{ valueAsNumber: true }}
                required={false}
              />
            </Grid>

            <Grid cols='2' gap='lg' className='w-full mx-0 mb-4'>
              <div className='mb-1'>
                <Controller
                  name={`${FIELD_PATH}.${index}.nonStackable`}
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      label='Non-stackable'
                      labelClassName='leading-none text-neutral-600 peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                      checked={field.value ?? false}
                      onCheckedChange={(checked) => field.onChange(checked)}
                    />
                  )}
                />
              </div>
              <div className='mb-1'>
                <Controller
                  name={`${FIELD_PATH}.${index}.hazmat`}
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      label='Hazmat'
                      labelClassName='leading-none text-neutral-600 peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                      checked={field.value ?? false}
                      onCheckedChange={(checked) => field.onChange(checked)}
                    />
                  )}
                />
              </div>
            </Grid>

            <Grid cols='2' gap='lg' align='end' className='w-full mx-0 mb-4'>
              <RHFTextInput
                name={`${FIELD_PATH}.${index}.unClass`}
                label='UN Class'
                placeholder=''
                required={false}
                readOnly={!isHazmat}
              />
              <RHFTextInput
                name={`${FIELD_PATH}.${index}.hazmatContactNumber`}
                label='Hazmat Contact Number'
                placeholder=''
                required={false}
                readOnly={!isHazmat}
              />
            </Grid>

            <Grid cols='2' gap='lg' className='w-full mx-0 mb-4'>
              <div>
                <Label
                  name={`${FIELD_PATH}.${index}.hazmatClass`}
                  className={ITEM_LABEL_CLASS_NAME}
                >
                  Hazmat Class
                </Label>
                <Controller
                  name={`${FIELD_PATH}.${index}.hazmatClass`}
                  control={control}
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ?? ''}
                      disabled={!isHazmat}
                    >
                      <SelectTrigger className='w-full mt-1'>
                        <SelectValue placeholder='Select' />
                      </SelectTrigger>
                      <SelectContent className={SELECT_CONTENT_MAX_HEIGHT}>
                        {HAZMAT_CLASS_OPTIONS_THREEG.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <span
                              className='truncate block max-w-[280px]'
                              title={opt.label}
                            >
                              {opt.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div>
                <Label
                  name={`${FIELD_PATH}.${index}.packingGroup`}
                  className={ITEM_LABEL_CLASS_NAME}
                >
                  Packing Group
                </Label>
                <Controller
                  name={`${FIELD_PATH}.${index}.packingGroup`}
                  control={control}
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ?? ''}
                      disabled={!isHazmat}
                    >
                      <SelectTrigger className='w-full mt-1'>
                        <SelectValue placeholder='Select' />
                      </SelectTrigger>
                      <SelectContent className={SELECT_CONTENT_MAX_HEIGHT}>
                        {PACKING_GROUP_OPTIONS_THREEG.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <span
                              className='truncate block max-w-[280px]'
                              title={opt.label}
                            >
                              {opt.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </Grid>

            <Grid cols='2' gap='lg' align='end' className='w-full mx-0 mb-4'>
              <RHFTextInput
                name={`${FIELD_PATH}.${index}.subsidiaryClass`}
                label='Subsidiary Class'
                placeholder=''
                required={false}
                readOnly={!isHazmat}
              />
              <RHFTextInput
                name={`${FIELD_PATH}.${index}.specialProvisions`}
                label='Special Provisions'
                placeholder=''
                required={false}
                readOnly={!isHazmat}
              />
            </Grid>

            <Grid cols='2' gap='lg' align='end' className='w-full mx-0 mb-4'>
              <RHFTextInput
                name={`${FIELD_PATH}.${index}.properShippingName`}
                label='Proper Shipping Name'
                placeholder=''
                required={false}
                readOnly={!isHazmat}
              />
              <RHFTextInput
                name={`${FIELD_PATH}.${index}.hazmatContactName`}
                label='Hazmat Contact Name'
                placeholder=''
                required={false}
                readOnly={!isHazmat}
              />
            </Grid>

            <div className='w-full mx-0 mb-4'>
              <RHFTextInput
                name={`${FIELD_PATH}.${index}.erapReference`}
                label='ERAP Reference'
                placeholder=''
                required={false}
                readOnly={!isHazmat}
              />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </motion.div>
  );

  const renderViewMode = () => {
    if (!watchedItem?.description) {
      return null;
    }

    const hasHandlingUnits = isValidNumber(watchedItem.handlingUnits);
    const hasPieces = isValidNumber(watchedItem.pieces);
    const hasWeight = isValidNumber(watchedItem.weight);
    const hasFreightClass = Boolean(watchedItem.freightClass);
    const hasNmfcCode = Boolean(watchedItem.nmfcCode);
    const dimensions = [
      watchedItem.length,
      watchedItem.width,
      watchedItem.height,
    ].filter(isValidNumber);
    const hasDimensions = dimensions.length > 0;

    const parts: JSX.Element[] = [];
    if (hasHandlingUnits) {
      parts.push(
        <Typography key='hu' variant='body-xs' className='text-neutral-500'>
          {watchedItem.handlingUnits} HU
          {watchedItem.handlingUnits !== 1 ? 's' : ''}
        </Typography>
      );
    }
    if (hasPieces) {
      parts.push(
        <Typography key='pc' variant='body-xs' className='text-neutral-500'>
          {watchedItem.pieces} pc{watchedItem.pieces !== 1 ? 's' : ''}
        </Typography>
      );
    }
    if (hasDimensions) {
      parts.push(
        <Typography key='dims' variant='body-xs' className='text-neutral-500'>
          {dimensions.join(' × ')} {dimensionUnit}
        </Typography>
      );
    }
    if (hasWeight) {
      parts.push(
        <Typography key='wt' variant='body-xs' className='text-neutral-500'>
          {watchedItem.weight} {weightUnit}
        </Typography>
      );
    }

    const secondLineParts: JSX.Element[] = [];
    if (hasNmfcCode) {
      secondLineParts.push(
        <Typography key='nmfc' variant='body-xs' className='text-neutral-500'>
          NMFC {watchedItem.nmfcCode}
        </Typography>
      );
    }
    if (hasFreightClass) {
      secondLineParts.push(
        <Typography key='fc' variant='body-xs' className='text-neutral-500'>
          FC: {watchedItem.freightClass}
        </Typography>
      );
    }
    if (itemCubicFeet > 0) {
      secondLineParts.push(
        <Typography key='cbf' variant='body-xs' className='text-neutral-500'>
          Cubic ft: {formatUpToFiveDecimals(itemCubicFeet)} ft³
        </Typography>
      );
    }
    if (pcf > 0) {
      secondLineParts.push(
        <Typography key='pcf' variant='body-xs' className='text-neutral-500'>
          PCF: {formatUpToFiveDecimals(pcf)} lbs/ft³
        </Typography>
      );
    }

    return (
      <motion.div
        key='view-mode'
        {...ANIMATIONS.toggle}
        className='overflow-hidden cursor-pointer'
        onClick={handlers.edit}
        onKeyDown={(e: React.KeyboardEvent) =>
          e.key === 'Enter' && handlers.edit()
        }
        role='button'
        tabIndex={0}
        aria-label='Click to edit item'
      >
        <Flex justify='between' align='center' className='min-w-0'>
          <Flex align='center' gap='sm' className='flex-1 min-w-0 mr-2'>
            <Package className='h-4 w-4 text-neutral-500 shrink-0' />
            <Typography
              variant='body-sm'
              weight='medium'
              className='truncate'
              title={watchedItem.description}
            >
              {watchedItem.description}
            </Typography>
            {renderSideLoadStatusTag('max-w-[170px]')}
          </Flex>
          <div onClick={(e) => e.stopPropagation()} className='shrink-0'>
            {renderActions()}
          </div>
        </Flex>
        {parts.length > 0 && (
          <Flex align='center' gap='sm' className='mt-1 ml-1 flex-wrap'>
            {parts.map((p, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className='text-neutral-300'>•</span>}
                {p}
              </React.Fragment>
            ))}
          </Flex>
        )}
        {secondLineParts.length > 0 && (
          <Flex align='center' gap='sm' className='mt-1 ml-1 flex-wrap'>
            {secondLineParts.map((p, i) => (
              <React.Fragment key={p.key ?? i}>
                {i > 0 && <span className='text-neutral-300'>•</span>}
                {p}
              </React.Fragment>
            ))}
          </Flex>
        )}
      </motion.div>
    );
  };

  return (
    <motion.div
      layout
      transition={{ type: 'spring', duration: 0.2, bounce: 0 }}
      {...ANIMATIONS.card}
      className='w-full overflow-hidden rounded-md border border-neutral-400 shadow-sm relative p-3 mb-2'
      style={
        showVisualizationColor
          ? {
              borderLeft: `3px solid ${getColorForItemId(
                watchedItem?.itemId ?? `item-${index}`,
                itemColorMap
              )}`,
            }
          : undefined
      }
      onKeyDown={handleKeyDown}
    >
      <AnimatePresence mode='wait'>
        {isEditing ? renderEditingForm() : renderViewMode()}
      </AnimatePresence>
    </motion.div>
  );
}
